import { useCallback, useEffect, useState } from 'react';
import { normaliseTopUpRecords, type TopUpRecord } from '@mypoz/shared';

import { fetchTopUpRows } from './api.js';

export interface TopUpHistory {
  readonly status: 'loading' | 'ready' | 'error';
  /** Newest first. Empty until ready. */
  readonly records: readonly TopUpRecord[];
  readonly retry: () => void;
}

/**
 * The account's top-up history from `get_trade` (MYP-107), loaded by the Wallet page when it
 * opens — not app-wide, since no other screen needs a 553-row list.
 */
export function useTopUpHistory(): TopUpHistory {
  const [records, setRecords] = useState<readonly TopUpRecord[]>([]);
  const [status, setStatus] = useState<TopUpHistory['status']>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Only the newest attempt's answer lands (StrictMode's double mount, or a retry mid-flight).
    let current = true;
    setStatus('loading');
    fetchTopUpRows().then(
      (rows) => {
        if (!current) return;
        setRecords(normaliseTopUpRecords(rows));
        setStatus('ready');
      },
      (cause: unknown) => {
        if (!current) return;
        // Logged, not shown: the vendor's reason is Chinese prose from its own code table.
        console.error('[wallet] could not load the top-up history', cause);
        setStatus('error');
      },
    );
    return () => {
      current = false;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  return { status, records, retry };
}
